import React, { useState } from "react";

import { Button, Classes, Collapse } from "@blueprintjs/core";
import {
  documentToReactComponents,
  Options,
} from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, Text } from "@contentful/rich-text-types";
import styled from "@emotion/styled";
import { useHead } from "@unhead/react";
import classnames from "classnames";
import idx from "idx";
import capitalize from "lodash/capitalize";
import lowerCase from "lodash/lowerCase";
import { Link, useSearchParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import {
  ArticleStructuredData,
  BreadcrumbStructuredData,
} from "@/components/helpers/seo/StructuredData";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { TOCBox } from "@/components/pages_logged_in/workspace-admin/WorkspaceMemberTable";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { RelatedArticlesList } from "@/components/pieces/article/RelatedArticlesList";
import { Mp3Player } from "@/components/pieces/audio/Mp3Player";
import { Container } from "@/components/pieces/bootstrap";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  BP_NS,
  INNER_BORDER_COLOR,
  LIGHT_BACKGROUND,
  LIGHTEST_BACKGROUND,
  MAINTEXT_COLOR,
  PUBLIC_CONTENT_MAX_WIDTH,
  SUBTEXT_COLOR,
} from "@/css/constants";
import { ArticleSeriesCanonicalDomain } from "@/graphql";
import { ArticleWithReadStatus, TOCItem } from "@/types/articles";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";

import { Callout } from "./Callout";
import { GPTBot } from "./GPTBot";
import { Hyperlink } from "./Hyperlink";
import { Prompt } from "./Prompt";
import { MIN_TOC_WIDTH, TableOfContents } from "./TableOfContents";
import { VideoEmbed } from "./VideoEmbed";

export const CARD_IMG_HEIGHT = 365;
export const MAX_CONTENT_WIDTH = 690;

function SidebarCTA({ article }: { article: ArticleWithReadStatus }) {
  const user = getUser();
  const { sidebarCta } = article || {};

  if (!sidebarCta || user) return null;
  const { headline, body, ctaLink, buttonText } = sidebarCta || {};

  return (
    <TOCBox mb={3} minWidth={MIN_TOC_WIDTH}>
      <Box className="header">{headline}</Box>
      <div className="mt-2 mb-3 text-sm text-tight">{body}</div>
      <FlexibleButton
        as={"a"}
        href={ctaLink ?? undefined}
        fill
        variant="primary"
        target="_parent"
      >
        {buttonText}
      </FlexibleButton>
      <Box className="bbs" my={4}></Box>
    </TOCBox>
  );
}

const slugify = (text: string) =>
  text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");

export function Article({ article }: { article: ArticleWithReadStatus }) {
  const tableOfContents: TOCItem[] = [];
  const user = getUser();
  const isLoggedIn = !!user;

  // Optimize hero image - 800px wide for retina on 690px container
  const imgUrl = article.heroImage
    ? `${article.heroImage}?w=800&h=450&q=85&fm=webp&fit=fill`
    : "";
  const seoImgUrl = article.heroImage ? `https:${article.heroImage}` : "";

  const renderOptions: Options = {
    renderNode: {
      [BLOCKS.HEADING_2]: (node, children) => {
        const nodeValue = (node.content[0] as Text).value;
        const nodeId = slugify(nodeValue);
        tableOfContents.push({ id: nodeId, label: nodeValue });
        return <h2 id={nodeId}>{children}</h2>;
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        const nodeValue = (node.content[0] as Text).value;
        const nodeId = slugify(nodeValue);
        tableOfContents.push({ id: nodeId, label: nodeValue, nested: true });
        return <h3 id={nodeId}>{children}</h3>;
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = node.data.target;
        if (asset && asset.type && asset.type.startsWith("image")) {
          return (
            <img
              src={`${asset.url}?fm=webp`}
              loading="lazy"
              alt={asset.title}
            />
          );
        } else if (
          asset &&
          asset.type &&
          (asset.type.startsWith("audio/mpeg") ||
            (asset.type.startsWith("video/mp4") && asset.url.endsWith(".m4a")))
        ) {
          return (
            <Box
              my={24}
              className="bas round-corners"
              px={18}
              py={24}
              style={{ backgroundColor: "#FFF" }}
            >
              <Mp3Player src={asset.url} />
            </Box>
          );
        } else {
          return null;
        }
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const entry = node.data.target;
        if (entry.type === "callout") {
          return <Callout entry={entry} />;
        } else if (entry.type === "prompt") {
          return <Prompt entry={entry} />;
        } else if (entry.type === "gptBot") {
          return <GPTBot entry={entry} />;
        } else if (entry.type === "videoEmbed") {
          return <VideoEmbed entry={entry} />;
        }
        return null;
      },
      [INLINES.HYPERLINK]: (node) => {
        return <Hyperlink node={node} />;
      },
      [BLOCKS.UL_LIST]: (_node, children) => {
        return (
          <ul className="list-disc pl-6 [&_ul]:list-[revert]">{children}</ul>
        );
      },
      [BLOCKS.OL_LIST]: (_node, children) => {
        return <ol className="list-decimal pl-6">{children}</ol>;
      },
      [BLOCKS.TABLE]: (_node, children) => {
        return (
          <div className="relative my-4 overflow-x-auto rounded-lg shadow-sm">
            <table className="w-full overflow-hidden rounded-lg text-left text-sm text-gray-700 dark:text-gray-400 [&_td_p]:m-0 [&_th_p]:m-0 [&_tr:first-child_th:first-child]:rounded-tl-lg [&_tr:first-child_th:last-child]:rounded-tr-lg [&_tr:last-child_td:first-child]:rounded-bl-lg [&_tr:last-child_td:last-child]:rounded-br-lg">
              {children}
            </table>
          </div>
        );
      },
      [BLOCKS.TABLE_ROW]: (_node, children) => {
        return <tr>{children}</tr>;
      },
      [BLOCKS.TABLE_CELL]: (_node, children) => {
        return (
          <td className="p-3 dark:border-gray-700 dark:bg-gray-800">
            {children}
          </td>
        );
      },
      [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => {
        return (
          <th className="border-b-1 bg-gray-200 p-3 text-left text-sm dark:bg-gray-700 dark:text-gray-400">
            {children}
          </th>
        );
      },
    },
  };

  let jsonData = null;
  try {
    jsonData = JSON.parse(article.body);
  } catch (e) {
    console.error("Error parsing article body JSON", e);
  }

  useHead({
    bodyAttrs: { class: "public-site" },
  });

  const [searchParams] = useSearchParams();
  const isBareLayout = searchParams.get("layout") === "bare";

  if (!jsonData) {
    return <NotFound />;
  }

  const articleContent = documentToReactComponents(jsonData, renderOptions);
  const canonicalDomain = article?.canonicalDomain;

  const articles = ensureNonNullArray(article?.primarySeries?.articles).filter(
    (a): a is ArticleWithReadStatus => a.id !== undefined,
  );
  const primarySeries = {
    ...article?.primarySeries,
    id: article?.primarySeries?.id ?? "",
    title: article?.primarySeries?.title ?? "",
    urlSlug: article?.primarySeries?.urlSlug ?? "",
    description: article?.primarySeries?.description ?? "",
    canonicalDomain:
      article?.primarySeries?.canonicalDomain ??
      ArticleSeriesCanonicalDomain.Exec,
    articles: articles,
    relatedSeries: ensureNonNullArray(article?.primarySeries?.relatedSeries),
  };

  return (
    <>
      <Box
        backgroundColor="#FFF"
        className="bbs"
        mb={[3, 4]}
        pt={[3, 36]}
        pb={24}
        mt={isBareLayout ? -60 : 0}
      >
        <Container maxWidth={isLoggedIn ? undefined : PUBLIC_CONTENT_MAX_WIDTH}>
          <Header article={article} isFullWidth={true} />
        </Container>
      </Box>
      <Container maxWidth={isLoggedIn ? undefined : PUBLIC_CONTENT_MAX_WIDTH}>
        <ArticleContainer style={isBareLayout ? { marginBottom: 40 } : {}}>
          <SEOHead
            title={`${article.title} | ${capitalize(canonicalDomain)} Learn`}
            description={article.summary}
            canonicalPath={`/learn/${article.urlSlug}`}
            canonicalDomain={`https://www.${lowerCase(canonicalDomain)}.com`}
            image={seoImgUrl}
            additionalHeadTags={
              article.noindex ? <meta name="robots" content="noindex" /> : null
            }
          />

          {/* Structured Data for SEO */}
          <ArticleStructuredData
            title={article.title || ""}
            description={article.summary}
            author={article.authorName}
            datePublished={article.updatedAt}
            dateModified={article.updatedAt}
            image={seoImgUrl}
            url={`https://www.${lowerCase(canonicalDomain)}.com/learn/${article.urlSlug}`}
          />

          <BreadcrumbStructuredData
            items={[
              {
                name: "Learn",
                url: `https://www.${lowerCase(canonicalDomain)}.com/learn`,
              },
              ...(article.primarySeries
                ? [
                    {
                      name: article.primarySeries.title || "",
                      url: `https://www.${lowerCase(canonicalDomain)}.com/learn/series/${
                        article.primarySeries.urlSlug
                      }`,
                    },
                  ]
                : []),
              {
                name: article.title || "",
                url: `https://www.${lowerCase(canonicalDomain)}.com/learn/${
                  article.urlSlug
                }`,
              },
            ]}
          />

          <Media lessThan="lg">
            {imgUrl ? (
              <HeroImageBox imgUrl={imgUrl} title={article.title} />
            ) : null}
            <TocMobile tableOfContents={tableOfContents} article={article} />
          </Media>
          <Box>
            <Flex
              flexWrap="wrap"
              justifyContent="space-between"
              width={1}
              style={{ overflow: "visible" }}
            >
              <Box flex="1 1 500px" maxWidth={[null, null, MAX_CONTENT_WIDTH]}>
                <Media greaterThan="md">
                  {imgUrl ? (
                    <HeroImageBox imgUrl={imgUrl} title={article.title} />
                  ) : null}
                </Media>
                <ArticleContent>{articleContent}</ArticleContent>
                <Author article={article} />
              </Box>
              <Media greaterThanOrEqual="lg">
                {(mediaClassNames, renderChildren) => {
                  if (renderChildren) {
                    return (
                      <Flex
                        className={mediaClassNames}
                        flex="0 0 400px"
                        justifyContent="start"
                        pl={[24, 24, 24, 24]}
                        flexDirection={"column"}
                        css={{
                          position: "sticky",
                          top: 70,
                          maxHeight: "calc(100vh - 90px)",
                          overflowY: "auto",
                        }}
                      >
                        <div className="px-4">
                          <SidebarCTA article={article} />
                          <TableOfContents items={tableOfContents} />
                        </div>
                      </Flex>
                    );
                  }
                }}
              </Media>
            </Flex>
          </Box>
        </ArticleContainer>
        {!isBareLayout && (
          <ArticleContainer>
            <RelatedArticlesList
              hasVisualSpace={true}
              articleSlug={article?.urlSlug ?? ""}
              primarySeries={primarySeries}
            />
          </ArticleContainer>
        )}
      </Container>
    </>
  );
}

function HeroImageBox({
  imgUrl,
  title,
}: {
  imgUrl: string;
  title?: string | null;
}) {
  // Optimize for max content width (690px) with 16:9 aspect ratio
  const heroWidth = 700;
  const heroHeight = Math.round(700 * (9 / 16)); // ~394px

  return (
    <Box
      className="hero-image"
      mb={[0, 24]}
      width={1}
      maxWidth={[null, null, MAX_CONTENT_WIDTH]}
      style={{
        borderRadius: "6px",
        border: `1px solid ${INNER_BORDER_COLOR}`,
        overflow: "hidden",
      }}
    >
      <img
        src={imgUrl}
        alt={title ?? undefined}
        width={heroWidth}
        height={heroHeight}
        loading="lazy"
        className="text-transparent"
        style={{
          width: "100%",
          height: "auto",
          fontSize: 0,
          backgroundColor: "#f3f4f6",
        }}
        onLoad={(e) => {
          (e.currentTarget as HTMLImageElement).style.fontSize = "";
        }}
      />
    </Box>
  );
}

interface TocMobileProps {
  tableOfContents: { id: string; label: string }[];
  article: ArticleWithReadStatus;
}

function TocMobile({ tableOfContents, article }: TocMobileProps) {
  const [isOpen, setOpen] = useState(false);
  const toggleTOC = () => {
    setOpen((o) => !o);
  };
  return (
    <StyledMobileToc
      className={classnames({ "toc--is-open": isOpen })}
      minHeight={44}
      width={1}
      my={12}
    >
      <Button
        onClick={toggleTOC}
        text="Table of Contents"
        fill={true}
        rightIcon={isOpen ? "chevron-up" : "chevron-down"}
        alignText="left"
        style={{
          backgroundColor: LIGHT_BACKGROUND,
          color: MAINTEXT_COLOR,
        }}
      />
      <Collapse isOpen={isOpen}>
        <Box
          width={1}
          p={3}
          style={{
            transition: "all 0.3s ease",
            border: `1px solid ${LIGHT_BACKGROUND}`,
            borderTop: 0,
          }}
        >
          <div className="hidden md:visible">
            <SidebarCTA article={article} />
          </div>
          <TableOfContents items={tableOfContents} />
        </Box>
      </Collapse>
    </StyledMobileToc>
  );
}

interface ArticleBreadcrumbProps {
  article: ArticleWithReadStatus;
}

function ArticleBreadcrumb({ article }: ArticleBreadcrumbProps) {
  const { primarySeries } = article || {};

  if (!primarySeries) return null;
  return (
    <Flex color={SUBTEXT_COLOR}>
      <Link to={`/learn`} style={{ color: MAINTEXT_COLOR }}>
        Learn
      </Link>
      {primarySeries && (
        <>
          <Box mx={1}>/</Box>
          <Link
            to={`/learn/series/${primarySeries?.urlSlug}`}
            style={{ color: SUBTEXT_COLOR }}
          >
            {primarySeries?.title}
          </Link>
        </>
      )}
    </Flex>
  );
}

interface HeaderProps {
  article: ArticleWithReadStatus;
  isFullWidth?: boolean;
}

const Header: React.FC<HeaderProps> = React.memo(function ArticleHeader({
  article,
  isFullWidth,
}) {
  const updatedTime = formatDate(article.updatedAt, "MMM d, yyyy");
  const { authorName, authorIsCoach, author } = article;
  const coachSlug = idx(author, (_) => _.agentProfile.coachSlug);
  const isCoach = idx(author, (_) => _.isAgent);
  const updateTimeText = `${article.timeToRead} min read • Updated ${updatedTime}`;
  const authorView = (
    <>
      {authorName && (
        <>
          {authorIsCoach && isCoach && coachSlug ? (
            <Link to={`/@${coachSlug}`}>{authorName}</Link>
          ) : (
            authorName
          )}
          {` • `}
        </>
      )}
    </>
  );

  return (
    <>
      <Media greaterThan="xs">
        {isFullWidth && <ArticleBreadcrumb article={article} />}
        <h1 className="font-medium text-3xl leading-tight max-w-3xl md:text-4xl max-md:text-4xl">
          {article.title}
        </h1>
        <Flex color={SUBTEXT_COLOR} mt={3}>
          {authorView}
          {updateTimeText}
        </Flex>
      </Media>
      <Media at="xs">
        <h1 className="font-semibold text-3xl leading-tight max-w-3xl">
          {article.title}
        </h1>
        <Box color={SUBTEXT_COLOR} fontSize={14} my={2}>
          {updateTimeText}
        </Box>
      </Media>
    </>
  );
});

interface AuthorProps {
  article: ArticleWithReadStatus;
}

const Author: React.FC<AuthorProps> = React.memo(({ article }) => {
  const {
    authorName,
    authorPhoto,
    authorShortBio,
    authorUrl,
    authorIsCoach,
    author,
  } = article;
  const coachSlug = author?.agentProfile?.coachSlug;
  const isCoach = author?.isAgent;
  const canRenderCoachProfileLink = authorIsCoach && isCoach && coachSlug;
  if (!authorName) return null;
  const link =
    authorUrl || (canRenderCoachProfileLink ? `/@${coachSlug}` : null);

  return (
    <Flex mt={4} pt={4} className="bts">
      <Box minWidth={60} mr={24}>
        <Avatar user={{ name: authorName, photo: authorPhoto }} size={60} />
      </Box>
      <Box>
        <Flex flexWrap="wrap">
          <Box fontWeight={600}>
            {link ? (
              <a href={link} target="_blank" rel="noopener noreferrer nofollow">
                {authorName}
              </a>
            ) : (
              authorName
            )}
          </Box>
          {authorIsCoach && <Box ml={2}>Coach</Box>}
        </Flex>
        {authorShortBio && <Box mt={1}>{authorShortBio}</Box>}
        {canRenderCoachProfileLink && (
          <Box mt={1}>
            <span>Looking for a Coach ?</span>
            <Link to={`/@${coachSlug}`}>
              <span>{` Work with ${authorName}`}</span>
            </Link>
          </Box>
        )}
      </Box>
    </Flex>
  );
});

const StyledMobileToc = styled(Box)`
  &.toc--is-open button {
    border-radius: 0;
  }

  border: 1px solid #f5f5fb;
  border-radius: 8px;

  & .${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
    box-shadow: none;
  }
  & .header {
    display: none;
  }
  & .${Classes.COLLAPSE_BODY} > div > div {
    margin-top: 0 !important;
    color: #f5f5fb;
  }
`;

const ArticleContainer = styled("div")`
  font-size: 18px;
  line-height: 28px;
  margin: 0px auto;
  background-color: ${LIGHTEST_BACKGROUND};

  .hero-image {
    img {
      margin: 0;
      aspect-ratio: attr(width) / attr(height);
    }
  }
  @media screen and (max-width: 425px) {
    .hero-image {
      img {
        width: 100%;
        height: auto;
      }
    }
  }

  h1 {
    font-weight: bold;
    font-size: 36px;
  }
  h2 {
    font-size: 30px;
    margin-top: -24px;
    padding-top: 60px;
  }
  h3 {
    font-size: 22px;
    margin-top: -36px;
    padding-top: 60px;
  }
  h4 {
    font-size: 18px;
    margin-top: 24px;
  }
  .${Classes.CARD} img {
    margin: 0;
  }

  h2,
  h3,
  h4 {
    font-weight: 500;
    font-family: Inter;
    line-height: 1.3;
  }

  h1 + h2,
  h1 + h3,
  h1 + h4,
  h2 + h3,
  h2 + h4,
  h3 + h4 {
    margin-top: -30px;
    padding-top: 40px;
  }

  blockquote {
    white-space: break-spaces;
  }
`;

const ArticleContent = styled("div")`
  img {
    max-width: 100%;
    margin: 16px 0;
  }
`;
